import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PAYMENT_MODES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader } from "@/components/ui-kit";
import { Wallet, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function InvoiceDetailDialog({ invoiceId, onOpenChange, onChanged }) {
  const [inv, setInv] = useState(null);
  const [modes, setModes] = useState(PAYMENT_MODES);
  const [accounts, setAccounts] = useState([]);
  const [pay, setPay] = useState({ montant: "", mode: "Espèces", account_id: "", date: new Date().toISOString().slice(0, 10), note: "" });

  const load = () => api.get("/invoices").then((r) => setInv(r.data.find((x) => x.id === invoiceId) || null));
  useEffect(() => {
    if (invoiceId) { setInv(null); load(); api.get("/settings").then((r) => r.data.payment_methods && setModes(r.data.payment_methods)).catch(() => {}); api.get("/cash-accounts").then((r) => setAccounts(r.data)).catch(() => {}); }
  }, [invoiceId]);

  const addPayment = async (e) => {
    e.preventDefault();
    const montant = Number(pay.montant);
    if (!montant || montant <= 0) { toast.error("Montant invalide"); return; }
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/payments`, { ...pay, montant });
      setInv(data); setPay({ montant: "", mode: "Espèces", account_id: "", date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("Paiement enregistré"); onChanged && onChanged();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const rmPayment = async (pid) => {
    try { const { data } = await api.delete(`/invoices/${invoiceId}/payments/${pid}`); setInv(data); onChanged && onChanged(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <Dialog open={!!invoiceId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-detail">
        {!inv ? <Loader /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-3">
                <Wallet className="h-5 w-5 text-brand-600" />Facture {inv.numero}
                <span className="font-normal text-sm text-slate-400">· {inv.client_name}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border bg-white p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Total TTC</p><p className="font-mono font-bold text-slate-800">{fmtMoney(inv.total_ttc)}</p></div>
              <div className="rounded-lg border bg-emerald-50 p-3"><p className="text-[10px] uppercase font-mono text-emerald-600">Payé</p><p className="font-mono font-bold text-emerald-700">{fmtMoney(inv.montant_paye)}</p></div>
              <div className="rounded-lg border bg-rose-50 p-3"><p className="text-[10px] uppercase font-mono text-rose-600">Solde dû</p><p className="font-mono font-bold text-rose-700">{fmtMoney(inv.solde)}</p></div>
            </div>
            <div className="flex items-center gap-2"><span className="text-sm text-slate-500">État :</span><StatusBadge value={inv.creance_etat} />{inv.echeance && <span className="text-xs text-slate-400 ml-auto">Échéance : {fmtDate(inv.echeance)}</span>}</div>

            <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-2">Règlements</p>
            <div className="space-y-1.5">
              {(inv.payments || []).length === 0 ? <p className="text-sm text-slate-400">Aucun règlement</p> :
                inv.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border bg-slate-50/60 px-3 py-2" data-testid={`payment-${p.id}`}>
                    <span className="text-xs text-slate-500 w-24">{fmtDate(p.date)}</span>
                    <span className="text-sm text-slate-700 flex-1 px-2">{p.mode}{p.note ? ` · ${p.note}` : ""}</span>
                    <span className="font-mono text-sm font-semibold text-emerald-700">{fmtMoney(p.montant)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-2" onClick={() => rmPayment(p.id)} data-testid={`payment-del-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                ))}
            </div>

            {inv.solde > 0 && inv.statut !== "Annulée" && (
              <form onSubmit={addPayment} className="rounded-lg border bg-white p-3 grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                <div><Label className="text-xs">Montant</Label><Input type="number" value={pay.montant} onChange={(e) => setPay({ ...pay, montant: e.target.value })} data-testid="payment-montant" className="mt-1 h-9" placeholder="0" /></div>
                <div><Label className="text-xs">Mode</Label>
                  <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}><SelectTrigger className="mt-1 h-9" data-testid="payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Caisse / Banque</Label>
                  <Select value={pay.account_id || "none"} onValueChange={(v) => setPay({ ...pay, account_id: v === "none" ? "" : v })}><SelectTrigger className="mt-1 h-9" data-testid="payment-account"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— Aucune —</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="mt-1 h-9" /></div>
                <Button type="submit" data-testid="payment-add" className="bg-brand-600 hover:bg-brand-700 text-white h-9"><Plus className="h-4 w-4 mr-1" />Encaisser</Button>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
